import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { IconInfoType } from 'svgIcons/MotionPortalIcons';
import { translate } from 'helpers/translateHelper';
import { updateSignal } from '../actions';
import { GeneralDialog } from 'sharedComponents/GeneralDialog';
import { useDispatch, useSelector } from 'react-redux';
import { getModel } from '../selectors';
import { getLimitValueRegex } from './helper';

const OffsetModal = ({ showOffset, setShowOffset, item }) => {
    const dispatch = useDispatch();
    const { currentTimeSeries } = useSelector(getModel);
    const title = `${translate('ABB.Powertrain.Frontend.editOffset')} | ${item.displayName}`;

    const [localOffsetValue, setLocalOffsetValue] = useState(item.offset || '');
    const [isDisable, setIsDisable] = useState(true);
    const [focusedInput, setFocusedInput] = useState(false);

    const filteredTimeSeries = currentTimeSeries?.find(timeSeriesItem => timeSeriesItem.uniqId === showOffset);

    useEffect(() => {
        if (filteredTimeSeries) {
            const signal = filteredTimeSeries;
            const isOffsetChanged = localOffsetValue !== '' && (localOffsetValue || '') !== (signal?.offset || '');
            setIsDisable(!isOffsetChanged);
        }
    }, [localOffsetValue]);

    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        const limitValueRegex = getLimitValueRegex(item?.precision ?? filteredTimeSeries?.timeseriesPrecision ?? filteredTimeSeries?.precision);
        // CHECKING OFFSET VALUE WITH MIN AND MAX VALUE FROM SIGNAL
        if (limitValueRegex.test(value)) {
            if (item?.maxValue && item?.minValue) {
                if (+item?.maxValue >= value && +item?.minValue <= value) {
                    setLocalOffsetValue(value);
                }
                return;
            }
            setLocalOffsetValue(value);
        }
    }, [filteredTimeSeries?.precision, filteredTimeSeries?.timeseriesPrecision, item?.maxValue, item?.minValue, item?.precision]);

    const handelSubmitOffset = useCallback(() => {
        dispatch(updateSignal(item.uniqId, 'offset', localOffsetValue));
        dispatch(updateSignal(item.uniqId, 'isModified', true));
        setShowOffset(null);
    }, [dispatch, item.uniqId, localOffsetValue, setShowOffset]);

    const handleCancel = useCallback(() => {
        const previousOffset = filteredTimeSeries?.offset ?? '';
        dispatch(updateSignal(item.uniqId, 'offset', previousOffset));
        dispatch(updateSignal(item.uniqId, 'isModified', false));
        setShowOffset(null);
    }, [filteredTimeSeries, dispatch, item.uniqId, setShowOffset]);

    return (
        <GeneralDialog
            show={showOffset}
            close={() => setShowOffset(null)}
            title={title}
            cancelButtonProps={{
                text: translate('ABB.Powertrain.Frontend.cancelLabel'),
                onClick: handleCancel
            }}
            acceptButtonProps={{
                text: translate('ABB.Powertrain.Frontend.applyBtnLbl'),
                onClick: handelSubmitOffset,
                disabled: isDisable
            }}
            closeButton={true}
            persistent={false}
            id={'limit_Offset'}
        >
            <div className='offset-info'>
                <IconInfoType color='#0051FF' />
                <p>{translate('ABB.Powertrain.Frontend.offsetModalDescription')}</p>
            </div>
            <div className='offset-input'>
                <div className='offset-input-title'>{translate('ABB.Powertrain.Frontend.calibrationValue')}</div>
                <div className={`limit-value-uom ${focusedInput ? 'highlight' : ''}`}>
                    <input
                        type='number'
                        placeholder={translate('ABB.Powertrain.Frontend.offsetValue')}
                        value={localOffsetValue}
                        onChange={handleInputChange}
                        name='limitOffset'
                        onFocus={() => { setFocusedInput(true); }}
                        onBlur={() => { setFocusedInput(false); }}
                    />
                    <div className='uom-text'>{item.unit}</div>
                </div>
            </div>
        </GeneralDialog>
    );
};

OffsetModal.propTypes = {
    showOffset: PropTypes.bool, // Whether the modal is visible
    setShowOffset: PropTypes.func, // Function to toggle the modal visibility
    item: PropTypes.shape({
        uniqId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Unique identifier for the item
        displayName: PropTypes.string, // Display name of the item
        offset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Current offset value
        precision: PropTypes.number, // Precision for offset value validation
        minValue: PropTypes.number, // Minimum allowed value for the offset
        maxValue: PropTypes.number, // Maximum allowed value for the offset
        unit: PropTypes.string, // Unit of measurement for the offset
    }), // The item being edited
};

export default React.memo(OffsetModal);
